import { useContext } from "react"
import PropTypes from "prop-types"
import { Paper } from "@mui/material"
import useGraphContext from "./context/graph"
import performanceTemplate from "./templates/performance"
import riskTemplate from "./templates/risk"
import exposureTemplate from "./templates/exposure"
import contentSetTemplate from "./templates/content-set"
import { historical as factorSensitivityHistorical, predicted as factorSensitivityPredicted } from "./templates/factor-sensitivity"
import { MODEL_METADATA_SHAPE } from "connected-components/asset-view/prop-types"
import { INSTRUMENTS_WITH_METADATA_SHAPE } from "connected-components/asset-view/prop-types"
import { usePanelId } from "connected-components/dashboard/context/panel-id"
import { CONTENT_SET_TEMPLATE_PREFIX } from "./components/template-selector"

const NAMED_TEMPLATES = {
  performance: performanceTemplate,
  risk: riskTemplate,
  exposure: exposureTemplate,
  "factor-sensitivity-historical": factorSensitivityHistorical,
  "factor-sensitivity-predicted": factorSensitivityPredicted,
}

function resolveTemplate(selectedTemplate) {
  if (selectedTemplate?.startsWith(CONTENT_SET_TEMPLATE_PREFIX)) return contentSetTemplate
  return NAMED_TEMPLATES[selectedTemplate] || NAMED_TEMPLATES.performance
}

export default function GraphWrapper(props) {
  const { context: parentContext, config } = props
  const panelId = usePanelId()
  const [GraphContext, contextValue] = useGraphContext(parentContext, config, panelId)

  return (
    <GraphContext.Provider value={contextValue}>
      <Graph
        {...{
          ...props,
          context: GraphContext,
        }}
      />
    </GraphContext.Provider>
  )
}

GraphWrapper.propTypes = {
  modelMetadata: MODEL_METADATA_SHAPE.isRequired,
  context: PropTypes.object.isRequired,
  instrumentsWithMetadata: INSTRUMENTS_WITH_METADATA_SHAPE.isRequired,
  config: PropTypes.object,
}

function Graph(props) {
  const { context, instrumentsWithMetadata, modelMetadata } = props
  const { selectedTemplate } = useContext(context)

  const template = resolveTemplate(selectedTemplate)

  const content = <div className="graph-wrapper">
    <Paper square className="graph-header" elevation={0}>
      <template.Header context={context} modelMetadata={modelMetadata} />
    </Paper>
    <template.Component
      context={context}
      instrumentsWithMetadata={instrumentsWithMetadata}
      modelMetadata={modelMetadata}
    />
  </div>

  if (template.Context) {
    return <template.Context>
      {content}
    </template.Context>
  }

  return content
}

Graph.propTypes = {
  modelMetadata: MODEL_METADATA_SHAPE.isRequired,
  context: PropTypes.object.isRequired,
  instrumentsWithMetadata: INSTRUMENTS_WITH_METADATA_SHAPE.isRequired,
}
